import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import api from "../lib/api";
import { apiError, formatAmount, formatDay, paymentDateLabel } from "../lib/finance";
import { PaymentBadge, ReceiptMissingBadge, WorkflowBadge } from "../components/finance/FinanceBadges";
import ExpenseDetailSheet from "../components/finance/ExpenseDetailSheet";
import ReceiptInbox from "../components/finance/ReceiptInbox";
import ContributionsPanel from "../components/finance/ContributionsPanel";
import SubscriptionsPanel, { SubscriptionDrafts } from "../components/finance/SubscriptionsPanel";
import ExportDialog from "../components/finance/ExportDialog";
import { ClaimSheet, ClaimsReview, MyClaims } from "../components/finance/ClaimsPanel";
import AdvancesPanel from "../components/finance/AdvancesPanel";
import CategoryCapDialog from "../components/finance/CategoryCapDialog";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { MobileSheet, MobileSheetBody, MobileSheetClose, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle, MobileSheetTrigger } from "../components/ui/mobile-sheet";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileWarning, Gauge, Inbox, MoreHorizontal, Plus, RefreshCw, Search, SlidersHorizontal, Trash2, X } from "lucide-react";

const ALL = "all";

function EmptyState({ title, text, action }) {
  return (
    <Card className="p-6 text-center space-y-2">
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{text}</p>
      {action && <div className="pt-2">{action}</div>}
    </Card>
  );
}

function ListSkeleton() {
  return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
}

export default function Finance() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canAll = hasPermission(user, "finance:view_all");
  const canSummary = hasPermission(user, "finance:view_summary");
  const canOwn = hasPermission(user, "finance:view_own_contributions");
  const canCreate = hasPermission(user, "finance:create_expense");
  const canEdit = hasPermission(user, "finance:edit_expense");
  const canDownload = hasPermission(user, "finance:view_attachments");
  const canManageCategories = hasPermission(user, "finance:manage_categories");
  const canDeleteCategories = canManageCategories && hasPermission(user, "finance:delete");
  const canManageSettings = hasPermission(user, "finance:manage_settings");
  const canContributions = hasPermission(user, "finance:view_contributions");
  const canManageSubscriptions = hasPermission(user, "finance:manage_subscriptions");
  const canClaim = hasPermission(user, "finance:submit_claims");
  const canApproveClaims = hasPermission(user, "finance:approve_claims");
  // An export never shows more than its reader may see: expenses need the full list, contributions their own permission.
  const canExport = hasPermission(user, "finance:export") && (canAll || canContributions);
  const [exportOpen, setExportOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [capFor, setCapFor] = useState(null); // 14-3: category whose cap is being edited
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => { // notes de frais waiting for this approver (never their own), shown on the « À approuver » view
    if (!canApproveClaims) return;
    api.get("/finance/claims").then(({ data }) => setReviewCount(data.items.filter((c) => !c.own).length)).catch(() => {});
  }, [canApproveClaims]);

  const [inbox, setInbox] = useState({ status: "loading", items: [], error: "" });
  const [subDrafts, setSubDrafts] = useState([]); // subscription drafts waiting for this responsible
  const waiting = inbox.items.length + subDrafts.length;
  // Sections of the page; « Contributions » and « Frais de mission » group several views, shown as a second row only when the
  // reader has more than one of them (a field account with only « Mes frais » sees it as a plain tab). `?tab=` keeps naming the view.
  const sections = [
    { key: "overview", label: "Vue d'ensemble", views: [canSummary && ["overview", "Vue d'ensemble"]] },
    { key: "inbox", label: waiting ? `À saisir (${waiting})` : "À saisir", views: [(canCreate || subDrafts.length > 0) && ["inbox", "À saisir"]] },
    { key: "expenses", label: "Dépenses", views: [canAll && ["expenses", "Dépenses"]] },
    { key: "contributions", label: "Contributions", views: [canContributions && ["contributions", "Tous les payeurs"], canOwn && ["mine", "Mes contributions"]] },
    { key: "frais", label: "Frais de mission", views: [canClaim && ["claims", "Mes frais"], canApproveClaims && ["review", "À approuver"],
      (canApproveClaims || canContributions) && ["advances", "Avances"]] },
    { key: "subscriptions", label: "Abonnements", views: [(canManageSubscriptions || canAll) && ["subscriptions", "Abonnements"]] },
    { key: "settings", label: "Paramètres", views: [(canManageCategories || canManageSettings) && ["settings", "Paramètres"]] },
  ].map((sec) => ({ ...sec, views: sec.views.filter(Boolean) })).filter((sec) => sec.views.length);
  const SOLO_LABELS = { mine: "Mes contributions", claims: "Mes frais", review: "Frais à approuver", advances: "Avances de mission" };
  const tabs = sections.map((sec) => [sec.views[0][0], sec.views.length === 1 ? (SOLO_LABELS[sec.views[0][0]] || sec.label) : sec.label, sec]);
  const requested = params.get("tab");
  const views = sections.flatMap((sec) => sec.views);
  const tab = views.some(([k]) => k === requested) ? requested : views[0]?.[0];
  const currentSection = sections.find((sec) => sec.views.some(([k]) => k === tab));
  const tabsRef = useRef(null);
  useEffect(() => { // on a phone the strip scrolls: keep the active section in view (opened from a link, or after a tap)
    const active = tabsRef.current?.querySelector("[aria-current=page]");
    if (active && tabsRef.current.scrollWidth > tabsRef.current.clientWidth) {
      const strip = tabsRef.current;
      const offset = active.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
      strip.scrollTo({ left: offset - (strip.clientWidth - active.offsetWidth) / 2 });
    }
  }, [currentSection?.key]);
  const setTab = (key) => setParams((p) => { const next = new URLSearchParams(p); next.set("tab", key); return next; }, { replace: true });

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [mine, setMine] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("EUR");
  const [categoryName, setCategoryName] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filters, setFilters] = useState({ query: "", status: ALL, payment: ALL, currency: ALL, receiptMissing: false });
  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  // « Paid by » is asked of the server (the list is capped at the 100 latest, so a person's older expenses must not be filtered away in the browser).
  const [payer, setPayer] = useState(ALL);
  const [payerOptions, setPayerOptions] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const payerRef = useRef(ALL);
  const fetchExpenses = useCallback(() => api.get("/finance/expenses", { params: { limit: 100, ...(payerRef.current !== ALL ? { payer: payerRef.current } : {}) } })
    .then(({ data }) => { setExpenses(data.items); setExpenseTotal(data.total); }), []);
  const changePayer = async (value) => {
    payerRef.current = value;
    setPayer(value);
    setListLoading(true);
    try { await fetchExpenses(); } catch (e) { toast.error(apiError(e)); } finally { setListLoading(false); }
  };

  // Legacy entry point (header "+" menu, older links): creation lives on its own page.
  useEffect(() => {
    if (location.state?.openCreate && canCreate) navigate("/finance/depenses/nouvelle", { replace: true });
  }, [location.state, canCreate, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const jobs = [];
      if (canSummary) jobs.push(api.get("/finance/dashboard").then(({ data }) => setSummary(data)));
      if (canAll) {
        jobs.push(fetchExpenses());
        jobs.push(api.get("/finance/expenses/payers").then(({ data }) => setPayerOptions(data.items)).catch(() => setPayerOptions([])));
      }
      if (canOwn) jobs.push(api.get("/finance/contributions/mine").then(({ data }) => setMine(data)));
      if (canManageCategories) jobs.push(api.get("/finance/categories").then(({ data }) => setCategories(data)));
      if (canCreate) {
        jobs.push(api.get("/finance/inbox")
          .then(({ data }) => setInbox({ status: "ready", items: data.items, error: "" }))
          .catch((e) => setInbox({ status: "error", items: [], error: apiError(e) })));
      }
      if (canEdit) jobs.push(api.get("/finance/subscriptions/drafts").then(({ data }) => setSubDrafts(data.items)).catch(() => setSubDrafts([])));
      if (canManageSettings) jobs.push(api.get("/finance/settings").then(({ data }) => { setCurrencies(data.currencies); setDefaultCurrency(data.default_currency); }));
      await Promise.all(jobs);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [canSummary, canAll, canOwn, canCreate, canEdit, canManageCategories, canManageSettings, fetchExpenses]);

  useEffect(() => { load(); }, [load]);

  // Coming back from the form page: open the expense that was just saved. Global search (Ctrl+K) lands here the same way with a note
  // de frais, a subscription, or « new subscription / new advance » to open; each is consumed once, then the state is cleared.
  const [landing, setLanding] = useState({});
  useEffect(() => {
    const st = location.state || {};
    if (!st.openExpense && !st.openClaim && !st.openSubscription && !st.newSubscription && !st.newAdvance) return;
    if (st.openExpense) setOpenId(st.openExpense);
    setLanding({ claim: st.openClaim || null, subscription: st.openSubscription || null, newSubscription: !!st.newSubscription, newAdvance: !!st.newAdvance });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const filtered = useMemo(() => {
    const q = filters.query.trim().toLocaleLowerCase("fr");
    return expenses.filter((x) =>
      (!q || `${x.title} ${x.supplier || ""} ${x.category_name || ""} ${x.project || ""} ${(x.payers || []).map((p) => p.label).join(" ")} ${x.created_by_name || ""}`.toLocaleLowerCase("fr").includes(q)) &&
      (filters.status === ALL || x.status === filters.status) &&
      (filters.payment === ALL || x.payment_status === filters.payment) &&
      (filters.currency === ALL || x.currency === filters.currency) &&
      (!filters.receiptMissing || x.receipt_missing));
  }, [expenses, filters]);
  const expenseCurrencies = useMemo(() => [...new Set(expenses.map((x) => x.currency))].sort(), [expenses]);
  const activeFilters = filters.query || payer !== ALL || filters.status !== ALL || filters.payment !== ALL || filters.currency !== ALL || filters.receiptMissing;
  const PAYMENT_FILTER_LABELS = { unpaid: "Non payées", partially_paid: "Partiellement payées", paid: "Payées" };
  const payerLabel = payerOptions.find((o) => o.key === payer)?.label || "cette personne";
  const activeChips = [
    payer !== ALL && ["payer", `Payé par ${payerLabel}`, () => changePayer(ALL)],
    filters.payment !== ALL && ["payment", PAYMENT_FILTER_LABELS[filters.payment], () => setFilter("payment", ALL)],
    filters.status !== ALL && ["status", filters.status === "draft" ? "Brouillons" : "Soumises", () => setFilter("status", ALL)],
    filters.currency !== ALL && ["currency", filters.currency, () => setFilter("currency", ALL)],
    filters.receiptMissing && ["receipt", "Sans justificatif", () => setFilter("receiptMissing", false)],
  ].filter(Boolean);
  const activeCount = activeChips.length;
  // Same controls and state on both layouts: inline on desktop, inside the "Filtres" popover on mobile.
  const filterControls = (
    <>
      <Select value={payer} onValueChange={changePayer}>
        <SelectTrigger className="w-full md:w-[190px]" aria-label="Filtrer par personne qui a payé" data-testid="finance-filter-payer"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Toutes les personnes</SelectItem>
          {payerOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label} ({o.count})</SelectItem>)}
          {payer !== ALL && !payerOptions.some((o) => o.key === payer) && <SelectItem value={payer}>{payerLabel}</SelectItem>}
        </SelectContent>
      </Select>
      <Select value={filters.payment} onValueChange={(v) => setFilter("payment", v)}>
        <SelectTrigger className="w-full md:w-[170px]" aria-label="Filtrer par paiement"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les paiements</SelectItem>
          <SelectItem value="unpaid">Non payées</SelectItem>
          <SelectItem value="partially_paid">Partiellement payées</SelectItem>
          <SelectItem value="paid">Payées</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
        <SelectTrigger className="w-full md:w-[150px]" aria-label="Filtrer par statut"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les statuts</SelectItem>
          <SelectItem value="draft">Brouillons</SelectItem>
          <SelectItem value="submitted">Soumises</SelectItem>
        </SelectContent>
      </Select>
      {expenseCurrencies.length > 1 && (
        <Select value={filters.currency} onValueChange={(v) => setFilter("currency", v)}>
          <SelectTrigger className="w-full md:w-[150px]" aria-label="Filtrer par devise"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes devises</SelectItem>
            {expenseCurrencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button type="button" variant={filters.receiptMissing ? "default" : "outline"} aria-pressed={filters.receiptMissing}
        onClick={() => setFilter("receiptMissing", !filters.receiptMissing)}
        className={`justify-start md:justify-center ${filters.receiptMissing ? "bg-red-700 hover:bg-red-800 text-white" : ""}`} data-testid="finance-filter-receipt">
        <FileWarning size={14} className="mr-1.5" /> Sans justificatif
      </Button>
    </>
  );

  const addCategory = async () => {
    try {
      const { data } = await api.post("/finance/categories", { name: categoryName.trim() });
      setCategories((old) => [...old, data].sort((a, b) => a.name.localeCompare(b.name, "fr")));
      setCategoryName("");
      toast.success(`Catégorie « ${data.name} » créée`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const saveCurrency = async (currency) => {
    try {
      await api.patch("/finance/settings", { default_currency: currency });
      setDefaultCurrency(currency);
      toast.success("Devise par défaut enregistrée");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const newButton = canCreate && (
    <Button onClick={() => navigate("/finance/depenses/nouvelle")} data-testid="finance-new-expense">
      <Plus size={15} className="mr-1" /> Nouvelle dépense
    </Button>
  );

  // Who paid, on the row itself: names of the active payments, « Jamora » for the company, a dash while nobody has paid.
  const payersText = (x) => (x.payers?.length ? x.payers.map((p) => p.label).join(", ") : "");
  const expenseRow = (x) => (
    <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpenId(x.id)} data-testid="finance-expense-row">
      <td className="px-3 py-2.5">
        <button type="button" className="text-left font-medium text-slate-900 hover:underline" onClick={(e) => { e.stopPropagation(); setOpenId(x.id); }}>{x.title}</button>
        <div className="text-xs text-slate-500 truncate max-w-[260px]">{[x.supplier, x.category_name].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="px-3 py-2.5 max-w-[200px]" data-testid="finance-expense-payers">
        {x.payers?.length ? <span className="block truncate text-slate-900" title={payersText(x)}>{payersText(x)}</span> : <span className="text-slate-400" aria-label="Personne n'a payé">—</span>}
        {x.created_by_name && <div className="text-xs text-slate-500 truncate" title={`Saisie par ${x.created_by_name}`}>Saisie par {x.created_by_name}</div>}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <PaymentBadge status={x.payment_status} />
        <div className="text-xs text-slate-500 mt-0.5">{x.payment_status === "unpaid" ? "Aucun paiement" : paymentDateLabel(x)}</div>
      </td>
      <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1"><WorkflowBadge status={x.status} />{x.receipt_missing && <ReceiptMissingBadge />}</div></td>
      <td className="px-3 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap">{formatAmount(x.amount, x.currency)}</td>
    </tr>
  );

  const expenseCard = (x) => (
    <button key={x.id} type="button" onClick={() => setOpenId(x.id)} className="w-full text-left p-3 space-y-1.5 hover:bg-slate-50" data-testid="finance-expense-card">
      <div className="flex justify-between gap-3">
        <span className="font-medium text-slate-900 break-words">{x.title}</span>
        <span className="tabular-nums font-semibold whitespace-nowrap">{formatAmount(x.amount, x.currency)}</span>
      </div>
      <div className="text-xs text-slate-500">{[x.supplier, x.category_name].filter(Boolean).join(" · ")}</div>
      <div className="text-xs text-slate-600" data-testid="finance-expense-payers">
        {x.payers?.length ? <>Payé par <span className="font-medium text-slate-900">{payersText(x)}</span></> : "Personne n'a payé"}
        {x.created_by_name && <span className="text-slate-500"> · saisie par {x.created_by_name}</span>}
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        <PaymentBadge status={x.payment_status} /><WorkflowBadge status={x.status} />{x.receipt_missing && <ReceiptMissingBadge />}
        {x.payment_status !== "unpaid" && <span className="text-xs text-slate-500">{paymentDateLabel(x)}</span>}
      </div>
    </button>
  );

  return (
    <div className="space-y-4" data-testid="finance-page">
      <header className="flex items-start justify-between gap-3 md:flex-wrap">
        <div className="min-w-0 flex-1 md:flex-none">
          <h1 className="font-display font-bold text-2xl text-slate-900">Finances internes</h1>
          <p className="text-sm text-slate-500">Les dépenses de Jamora, qui les a payées et leurs justificatifs.</p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button variant="outline" size="icon" onClick={load} disabled={loading} aria-label="Actualiser"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></Button>
          {canExport && (
            <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="finance-export-open">
              <Download size={15} className="mr-1.5" /> <span className="hidden sm:inline">Exporter</span><span className="sr-only sm:hidden">Exporter</span>
            </Button>
          )}
          {canCreate && (
            <Button variant="outline" onClick={() => navigate("/finance/import")} data-testid="finance-import-open">
              <FileSpreadsheet size={15} className="mr-1.5" /> <span className="hidden sm:inline">Importer un tableur</span><span className="sm:hidden">Importer</span>
            </Button>
          )}
          {newButton}
        </div>
        <MobileSheet>
          <MobileSheetTrigger asChild>
            <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Actions finances" data-testid="finance-actions-mobile">
              <MoreHorizontal size={20} />
            </Button>
          </MobileSheetTrigger>
          <MobileSheetContent className="md:hidden" aria-describedby={undefined}>
            <MobileSheetHeader>
              <MobileSheetTitle>Actions finances</MobileSheetTitle>
              <MobileSheetCloseButton />
            </MobileSheetHeader>
            <MobileSheetBody>
              <div className="space-y-2">
                <MobileSheetClose asChild>
                  <Button variant="outline" className="w-full justify-start gap-3" onClick={load} disabled={loading}>
                    <RefreshCw size={18} /> Actualiser
                  </Button>
                </MobileSheetClose>
                {canExport && <MobileSheetClose asChild>
                  <Button variant="outline" className="w-full justify-start gap-3" onClick={() => setExportOpen(true)} data-testid="finance-export-mobile">
                    <Download size={18} /> Exporter
                  </Button>
                </MobileSheetClose>}
                {canCreate && <>
                  <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start gap-3" onClick={() => navigate("/finance/import")} data-testid="finance-import-mobile">
                      <FileSpreadsheet size={18} /> Importer un tableur
                    </Button>
                  </MobileSheetClose>
                  <MobileSheetClose asChild>
                    <Button className="w-full justify-start gap-3" onClick={() => navigate("/finance/depenses/nouvelle")} data-testid="finance-new-expense-mobile">
                      <Plus size={18} /> Nouvelle dépense
                    </Button>
                  </MobileSheetClose>
                </>}
              </div>
            </MobileSheetBody>
          </MobileSheetContent>
        </MobileSheet>
      </header>

      {tabs.length > 1 && (
        // The bottom rule is an inset shadow, not a border: tabs overlapping a border made the scrolling strip 1 px taller
        // than itself, which showed a vertical scrollbar.
        <nav ref={tabsRef} aria-label="Sections Finance" className="flex gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_#e2e8f0]">
          {tabs.map(([key, label, sec]) => {
            const active = currentSection?.key === sec.key;
            return (
              <button key={sec.key} type="button" onClick={() => setTab(key)} aria-current={active ? "page" : undefined} data-testid={`finance-tab-${sec.views.length > 1 ? sec.key : key}`}
                className={`px-3 py-2 whitespace-nowrap text-sm border-b-2 rounded-t-md outline-none focus-visible:ring-2 focus-visible:ring-jamora/40 ${active ? "border-jamora text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                {label}
              </button>
            );
          })}
        </nav>
      )}
      {currentSection?.views.length > 1 && (
        <nav aria-label={currentSection.label} className="inline-flex max-w-full overflow-x-auto rounded-lg bg-slate-100 p-1 gap-1">
          {currentSection.views.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key ? "page" : undefined} data-testid={`finance-view-${key}`}
              className={`px-3 py-1.5 whitespace-nowrap text-sm rounded-md outline-none focus-visible:ring-2 focus-visible:ring-jamora/40 ${tab === key ? "bg-white text-slate-900 font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {label}{key === "review" && reviewCount > 0 ? ` (${reviewCount})` : ""}
            </button>
          ))}
        </nav>
      )}

      {!tabs.length && (
        <EmptyState title="Saisir une dépense" text="Vous pouvez enregistrer des dépenses, mais pas consulter la liste complète." action={newButton} />
      )}

      {tab === "overview" && (
        <section className="space-y-4" aria-label="Vue d'ensemble">
          {inbox.items.length > 0 && (
            <button type="button" onClick={() => setTab("inbox")} data-testid="finance-overview-inbox"
              className="w-full flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 text-left hover:bg-amber-100">
              <Inbox size={16} className="shrink-0" />
              <span>{inbox.items.length} justificatif{inbox.items.length > 1 ? "s" : ""} déposé{inbox.items.length > 1 ? "s" : ""} en attente de saisie. Les saisir</span>
            </button>
          )}
          {loading ? <ListSkeleton /> : !summary?.has_data ? (
            <EmptyState title="Aucune dépense pour le moment" text="Enregistrez votre première dépense avec son justificatif pour commencer le suivi." action={newButton} />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(summary.totals).map(([currency, total]) => (
                  <Card key={currency} className="p-4">
                    <div className="text-xs font-medium text-slate-500">Dépenses en {currency}</div>
                    <div className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{formatAmount(total, currency)}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {summary.counts[currency]} dépense{summary.counts[currency] > 1 ? "s" : ""} · Reste à payer {formatAmount(summary.outstanding[currency], currency)}
                    </div>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-slate-500">Un total par devise, sans conversion.</p>
              {summary.receipts_missing > 0 && canAll && (
                <button type="button" onClick={() => { setFilters({ query: "", status: ALL, payment: ALL, currency: ALL, receiptMissing: true }); setTab("expenses"); }}
                  className="w-full flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 text-left hover:bg-red-100">
                  <FileWarning size={16} className="shrink-0" />
                  <span>{summary.receipts_missing} dépense{summary.receipts_missing > 1 ? "s" : ""} sans justificatif. Voir la liste</span>
                </button>
              )}
              {canAll && expenses.length > 0 && (
                <div>
                  <h3 className="font-display font-semibold text-slate-900 mb-2">Dernières dépenses</h3>
                  <Card className="divide-y divide-slate-100 overflow-hidden">{expenses.slice(0, 5).map(expenseCard)}</Card>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {tab === "inbox" && (
        <div className="space-y-4">
          <SubscriptionDrafts items={subDrafts} onOpen={(d) => navigate(`/finance/depenses/${d.id}/modifier`)} />
          {canCreate && (loading && inbox.status === "loading" ? <ListSkeleton /> : <ReceiptInbox inbox={inbox} onChanged={load} />)}
        </div>
      )}

      {tab === "subscriptions" && <SubscriptionsPanel canManage={canManageSubscriptions} onOpenExpense={canAll ? setOpenId : null}
        initialOpenId={landing.subscription} startCreate={landing.newSubscription} />}

      {tab === "claims" && <MyClaims />}
      {tab === "review" && <ClaimsReview onCount={setReviewCount} />}
      {tab === "advances" && <AdvancesPanel startCreate={landing.newAdvance} />}

      {tab === "contributions" && <ContributionsPanel canExport={hasPermission(user, "finance:export")} canDecide={hasPermission(user, "finance:record_decisions")} />}

      {tab === "expenses" && (
        <section className="space-y-3" aria-label="Dépenses">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[12rem]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <Input value={filters.query} onChange={(e) => setFilter("query", e.target.value)} placeholder="Rechercher une dépense"
                aria-label="Rechercher une dépense" className="pl-9 text-base md:text-sm" />
            </div>
            <div className="hidden md:flex flex-wrap gap-2 md:basis-full xl:basis-auto">{filterControls}</div>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="md:hidden shrink-0" aria-label="Filtres" data-testid="finance-mobile-filters">
                  <SlidersHorizontal size={15} />{activeCount > 0 && <span className="ml-1.5 text-xs font-semibold">{activeCount}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[min(18rem,calc(100vw-2rem))] flex flex-col gap-2">{filterControls}</PopoverContent>
            </Popover>
          </div>
          {activeChips.length > 0 && (
            <div className="md:hidden flex flex-wrap gap-1.5">
              {activeChips.map(([key, label, reset]) => (
                <button key={key} type="button" onClick={reset} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700">
                  {label} <X size={12} aria-label="Retirer" />
                </button>
              ))}
            </div>
          )}

          {loading || listLoading ? <ListSkeleton /> : !expenses.length && payer === ALL ? (
            <EmptyState title="Aucune dépense enregistrée" text="Les dépenses saisies par l'équipe apparaîtront ici, avec leur statut de paiement et leurs justificatifs." action={newButton} />
          ) : !filtered.length ? (
            <EmptyState title="Aucune dépense ne correspond" text="Modifiez ou retirez les filtres pour voir d'autres dépenses."
              action={<Button variant="outline" onClick={() => { setFilters({ query: "", status: ALL, payment: ALL, currency: ALL, receiptMissing: false }); if (payer !== ALL) changePayer(ALL); }}>Retirer les filtres</Button>} />
          ) : (
            <Card className="overflow-hidden">
              <table className="hidden md:table w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                  <tr><th className="px-3 py-2.5">Dépense</th><th className="px-3 py-2.5">Payé par</th><th className="px-3 py-2.5">Paiement</th><th className="px-3 py-2.5">Statut</th><th className="px-3 py-2.5 text-right">Montant</th></tr>
                </thead>
                <tbody>{filtered.map(expenseRow)}</tbody>
              </table>
              <div className="md:hidden divide-y divide-slate-100">{filtered.map(expenseCard)}</div>
            </Card>
          )}
          {!loading && !listLoading && expenseTotal > expenses.length && (
            <p className="text-xs text-slate-500">Les {expenses.length} dépenses les plus récentes sont affichées (sur {expenseTotal}).</p>
          )}
          {activeFilters && !loading && filtered.length > 0 && <p className="text-xs text-slate-500">{filtered.length} dépense{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""}.</p>}
        </section>
      )}

      {tab === "mine" && (
        <section className="space-y-3" aria-label="Mes contributions">
          {loading ? <ListSkeleton /> : (
            <>
              {Object.keys(mine?.totals || {}).length > 0 && (
                <div className="grid gap-3 sm:grid-cols-3">
                  {Object.entries(mine.totals).map(([currency, total]) => (
                    <Card key={currency} className="p-4">
                      <div className="text-xs font-medium text-slate-500">Vous avez payé ({currency})</div>
                      <div className="text-xl font-semibold text-slate-900 tabular-nums mt-1">{formatAmount(total, currency)}</div>
                    </Card>
                  ))}
                </div>
              )}
              {!mine?.items?.length ? (
                <EmptyState title={mine?.collaborator_linked === false ? "Compte non lié" : "Aucune contribution"}
                  text={mine?.collaborator_linked === false ? "Votre compte n'est lié à aucune fiche Collaborateur. Demandez à un administrateur de vérifier la liaison." : "Les dépenses que vous avez payées personnellement apparaîtront ici."} />
              ) : (
                <Card className="divide-y divide-slate-100">
                  {mine.items.map((x, i) => (
                    <div key={`${x.expense_id}-${i}`} className="p-3 flex justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 break-words">{x.title}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="text-xs text-slate-500">{x.paid_at ? `Payé le ${formatDay(x.paid_at)}` : "Date de paiement manquante"}</span>
                          <WorkflowBadge status={x.status} />
                        </div>
                      </div>
                      <strong className="tabular-nums whitespace-nowrap">{formatAmount(x.amount, x.currency)}</strong>
                    </div>
                  ))}
                </Card>
              )}
              <p className="text-xs text-slate-500">Ces montants indiquent ce que vous avez payé. Ils ne valent pas créance : aucun remboursement n'est décidé ici.</p>
            </>
          )}
        </section>
      )}

      {tab === "settings" && (
        <section className="grid gap-4 md:grid-cols-2 items-start" aria-label="Paramètres Finance">
          {canManageCategories && (
            <Card className="p-4 space-y-3 min-w-0">
              <div>
                <h3 className="font-display font-semibold text-slate-900">Catégories</h3>
                <p className="text-xs text-slate-500">Pour regrouper les dépenses. Ex. : Logiciels, Déplacements, Matériel. Un plafond facultatif signale les notes de frais qui le dépassent.</p>
              </div>
              {categories.length ? (
                <ul className="divide-y divide-slate-100 rounded-md border border-slate-100" data-testid="finance-categories">
                  {categories.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 text-sm">
                      <span className="flex-1 min-w-[10rem] truncate text-slate-900">{c.name}</span>
                      {c.cap ? (
                        <span className="rounded-full bg-jamora-50 px-2 py-0.5 text-xs font-medium text-jamora tabular-nums whitespace-nowrap">Plafond {formatAmount(c.cap, c.cap_currency)}</span>
                      ) : <span className="text-xs text-slate-400 whitespace-nowrap">Sans plafond</span>}
                      <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => setCapFor(c)} data-testid={`category-cap-${c.id}`}
                        aria-label={`${c.cap ? "Modifier" : "Définir"} le plafond de « ${c.name} »`}>
                        <Gauge size={14} className="mr-1.5" />{c.cap ? "Modifier" : "Définir"}
                      </Button>
                      {canDeleteCategories && (
                        <Button variant="outline" size="icon" className="h-8 w-8 shrink-0 text-red-700" onClick={() => setDeletingCategory(c)}
                          data-testid={`category-delete-${c.id}`} aria-label={`Supprimer la catégorie « ${c.name} »`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : <span className="text-sm text-slate-500">Aucune catégorie.</span>}
              <div className="flex gap-2">
                <Input value={categoryName} onChange={(e) => setCategoryName(e.target.value)} maxLength={100} placeholder="Nouvelle catégorie" aria-label="Nouvelle catégorie" className="text-base md:text-sm"
                  onKeyDown={(e) => { if (e.key === "Enter" && categoryName.trim()) addCategory(); }} />
                <Button onClick={addCategory} disabled={!categoryName.trim()}>Ajouter</Button>
              </div>
            </Card>
          )}
          {canManageSettings && (
            <Card className="p-4 space-y-3">
              <div>
                <h3 className="font-display font-semibold text-slate-900">Devise par défaut</h3>
                <p className="text-xs text-slate-500">Proposée à chaque nouvelle dépense. Chaque dépense garde sa propre devise.</p>
              </div>
              <Select value={defaultCurrency} onValueChange={saveCurrency}>
                <SelectTrigger className="w-[160px]" aria-label="Devise par défaut"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Card>
          )}
        </section>
      )}

      <ClaimSheet claimId={landing.claim} onClose={() => setLanding((l) => ({ ...l, claim: null }))} />
      <DeleteWithReasonDialog
        target={deletingCategory} testId="delete-category" onClose={() => setDeletingCategory(null)}
        title={`Supprimer la catégorie « ${deletingCategory?.name || ""} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une catégorie déjà utilisée par une dépense, une note de frais ou un abonnement ne peut pas être supprimée."
        onConfirm={async (reason) => {
          const target = deletingCategory;
          try {
            await api.delete(`/finance/categories/${target.id}`, { data: { reason } });
            setCategories((old) => old.filter((c) => c.id !== target.id));
            toast.success("Mise à la corbeille");
          } catch (e) {
            toast.error(apiError(e));
          } finally {
            setDeletingCategory(null);
          }
        }}
      />
      <CategoryCapDialog category={capFor} defaultCurrency={defaultCurrency} onClose={() => setCapFor(null)}
        onSaved={(saved) => setCategories((old) => old.map((c) => (c.id === saved.id ? saved : c)))} />
      {canExport && <ExportDialog open={exportOpen} onOpenChange={setExportOpen} canExpenses={canAll} canContributions={canContributions} />}
      <ExpenseDetailSheet expenseId={openId} open={Boolean(openId)} onOpenChange={(o) => { if (!o) setOpenId(null); }}
        canEdit={canEdit} canDownload={canDownload} onChanged={load} />
    </div>
  );
}
